import { CLEAR_PRIO_CACHE } from '../../../actions';
import { ExternalOffice } from '../../../models/Office';
import {
  FETCH_EXTERNAL_OFFICES_COMMIT,
  FETCH_EXTERNAL_OFFICES_REQUEST,
  FETCH_EXTERNAL_OFFICES_ROLLBACK,
} from '../actions';
import { combineReducers, Reducer } from 'redux';
import { distinct } from '../../../util';
import { OfficeId } from '../../../models/Types';
import equals from 'deep-equal';

export interface ExternalOfficesState {
  byId: ExternalOfficesByIdState;
  ids: string[];
  meta: ExternalOfficesMetaState;
}

export interface ExternalOfficesByIdState {
  [officeId: string]: ExternalOffice;
}

interface ExternalOfficesAction {
  type: string;
  payload?: ExternalOffice[];
}

interface ExternalOfficesMetaState {
  isFetching: boolean;
  hasError: boolean;
  errorMessage?: string;
}

const byId: Reducer<ExternalOfficesByIdState, ExternalOfficesAction> = (
  state = {},
  action
) => {
  switch (action.type) {
    case FETCH_EXTERNAL_OFFICES_COMMIT: {
      const { payload } = action;
      return payload.reduce(function (map, item) {
        map[item.officeId] = item;
        return map;
      }, state);
    }
    case CLEAR_PRIO_CACHE: {
      return {};
    }
    default:
      return state;
  }
};

const ids: Reducer<string[], ExternalOfficesAction> = (state = [], action) => {
  switch (action.type) {
    case FETCH_EXTERNAL_OFFICES_COMMIT: {
      const { payload } = action;
      return distinct([...state, ...payload.map((item) => item.officeId)]);
    }
    case CLEAR_PRIO_CACHE: {
      return [];
    }
    default:
      return state;
  }
};

const meta: Reducer<ExternalOfficesMetaState, ExternalOfficesAction> = (
  state = { isFetching: false, hasError: false },
  action
) => {
  switch (action.type) {
    case FETCH_EXTERNAL_OFFICES_REQUEST: {
      return {
        ...state,
        isFetching: true,
      };
    }
    case FETCH_EXTERNAL_OFFICES_COMMIT: {
      return {
        ...state,
        isFetching: false,
      };
    }
    case FETCH_EXTERNAL_OFFICES_ROLLBACK: {
      return {
        ...state,
        isFetching: false,
        hasError: true,
        errorMessage: 'companies:errorMessages.fetchExternalOfficesError',
      };
    }
    case CLEAR_PRIO_CACHE: {
      return { isFetching: false, hasError: false };
    }
    default:
      return state;
  }
};

export default combineReducers<ExternalOfficesState>({
  byId,
  ids,
  meta,
});

export const getExternalOfficesByIdState: (
  state: ExternalOfficesState
) => ExternalOfficesByIdState = (state) => state.byId;

export const getExternalOfficeIds: (state: ExternalOfficesState) => string[] = (
  state
) => state.ids;

export const getExternalOffice: (
  state: ExternalOfficesState,
  officeId: OfficeId
) => ExternalOffice = (state, officeId) => {
  if (equals(state.byId, {})) {
    return undefined;
  }
  return state.byId[officeId] ?? undefined;
};

export const getAllExternalOffices: (
  state: ExternalOfficesState
) => ExternalOffice[] = (state) => {
  const offices = state.ids?.map((id) => state.byId[id]);
  return offices;
};

export const getIsFetching: (state: ExternalOfficesState) => boolean = (
  state
) => state.meta.isFetching;

export const getHasError: (state: ExternalOfficesState) => boolean = (state) =>
  state.meta.hasError;

export const getErrorMessage: (state: ExternalOfficesState) => string = (
  state
) => state.meta.errorMessage;
